import React, { useState, useEffect } from 'react';
import {
  Table, Badge, Alert, Spinner, Button
} from 'reactstrap';
import { RefreshCw, FileText, AlertCircle } from 'lucide-react';
import API from '../utils/api';

const SourceHistory = ({ source, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [source]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await API.get(`/sources/${source.id}/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (started, completed) => {
    if (!started || !completed) return 'N/A';
    const start = new Date(started);
    const end = new Date(completed);
    const diff = Math.floor((end - start) / 1000);
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-2">Loading history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        <AlertCircle size={20} className="me-2" />
        Failed to load history: {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-5">
        <FileText size={48} className="text-muted mb-3" />
        <h5 className="text-muted">No import history</h5>
        <p className="text-muted">This source has not been imported yet.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h6 className="text-white">Import History</h6>
        <Button size="sm" color="secondary" onClick={loadHistory}>
          <RefreshCw size={14} className="me-1" />
          Refresh
        </Button>
      </div>

      <Table dark hover responsive size="sm">
        <thead>
          <tr>
            <th>Batch ID</th>
            <th>Started</th>
            <th>Duration</th>
            <th>Total</th>
            <th>Imported</th>
            <th>Failed</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {history.map((record, idx) => (
            <tr key={idx}>
              <td>
                <small className="font-monospace">
                  {record[0]?.substring(0, 8)}...
                </small>
              </td>
              <td>{formatDate(record[1])}</td>
              <td>{formatDuration(record[1], record[2])}</td>
              <td>{record[3] || 0}</td>
              <td>
                <Badge color="success">{record[4] || 0}</Badge>
              </td>
              <td>
                <Badge color="danger">{record[5] || 0}</Badge>
              </td>
              <td>
                {record[4] > 0 ? (
                  <Badge color="success">Success</Badge>
                ) : record[5] > 0 ? (
                  <Badge color="danger">Failed</Badge>
                ) : (
                  <Badge color="warning">Partial</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="mt-3">
        <Button color="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default SourceHistory;